'use client'

import { Suspense } from 'react'

import { useIsClient } from 'usehooks-ts'

import { useBreakpoints } from '@/shared/hooks/useBreakpoints'
import { NavOverflowProvider } from '@/shared/contexts/NavOverflow'
import { cn } from '@/shared/lib/utils'
import { GlobalPrefetch } from '@/shared/ui/GlobalPrefetch'
import { NavigationProgress } from '@/shared/ui/NavigationProgress'

import { ShellMainNav } from './MainNav'
import { ShellRoot } from './Root'
import { ShellTopbar } from './Topbar'

interface ShellProps {
  children: React.ReactNode
}

export const Shell = ({ children }: ShellProps) => {
  const { isAtLeast } = useBreakpoints()
  const isClient = useIsClient()

  if (!isClient) return null

  return (
    <>
      <GlobalPrefetch />
      <Suspense fallback={null}>
        <NavigationProgress />
      </Suspense>
      <NavOverflowProvider>
        {isAtLeast('lg') ? (
          <ShellRoot className="relative flex justify-center w-full">
            <div
              className={cn(
                isAtLeast('xl') ? 'w-[256px]' : 'w-[88px]',
                'relative shrink-0'
              )}
            >
              <ShellMainNav className="sticky top-0 z-10 h-svh flex-col px-4 pt-10 pb-6" />
            </div>

            <div className="grow shrink max-w-4xl min-w-[368px]">{children}</div>

            <div className="w-[368px] shrink-0">
              <div className="sticky--disabled top-3--disabled">
                <ShellTopbar />
              </div>
            </div>
          </ShellRoot>
        ) : (
          <ShellRoot>
            <ShellTopbar className={cn('fixed top-0 z-50')} />
            {children}
            <ShellMainNav
              className={cn(
                'fixed bottom-0 flex h-[68px] items-center justify-center px-3 w-full bg-background shadow-[0px_-3px_8px_rgba(0,0,0,0.45)]'
              )}
            />
          </ShellRoot>
        )}
      </NavOverflowProvider>
    </>
  )
}
